import { create } from 'zustand'

/**
 * What the connection banner shows.
 *
 * Zustand rather than TanStack Query: no server owns it, and the websocket
 * client writes it from outside React.
 *
 * The three flags are kept separate rather than folded into one status enum
 * because that is how the Vue app drives them and the combinations are not all
 * exclusive -- going offline while already failed sets `reconnecting` without
 * clearing `failed`. `connectionStatus` derives the single value the banner
 * actually renders, so the precedence lives in one readable place instead of
 * being spread across the assignments.
 */
export interface ConnectionState {
  connected: boolean
  /** Trying again, either on a timer or waiting for the network to return. */
  reconnecting: boolean
  /** Given up: a rejected credential, or the attempt budget spent. */
  failed: boolean
  /**
   * The server could not replay what was missed, so what is on screen may be
   * stale in ways no event will correct. Only a refetch fixes it.
   */
  workspaceOutdated: boolean
}

export interface ConnectionActions {
  setConnected: (connected: boolean) => void
  setReconnecting: (reconnecting: boolean) => void
  setFailed: (failed: boolean) => void
  setWorkspaceOutdated: (outdated: boolean) => void
  reset: () => void
}

const initialState = (): ConnectionState => ({
  connected: false,
  reconnecting: false,
  failed: false,
  workspaceOutdated: false,
})

export const useConnectionStore = create<ConnectionState & ConnectionActions>((set) => ({
  ...initialState(),
  setConnected: (connected) => set({ connected }),
  setReconnecting: (reconnecting) => set({ reconnecting }),
  setFailed: (failed) => set({ failed }),
  setWorkspaceOutdated: (workspaceOutdated) => set({ workspaceOutdated }),
  reset: () => set(initialState()),
}))

export type ConnectionStatus = 'connected' | 'reconnecting' | 'failed' | 'offline'

/**
 * The one value the banner renders.
 *
 * `failed` outranks `reconnecting`: once the budget is spent the retries have
 * stopped, and still showing "reconnecting" promises something that is no longer
 * happening.
 */
export function connectionStatus(state: ConnectionState): ConnectionStatus {
  if (state.connected) return 'connected'
  if (state.failed) return 'failed'
  if (state.reconnecting) return 'reconnecting'
  return 'offline'
}

/** Reads the banner state outside React, for the websocket client. */
export function getConnection(): ConnectionState & ConnectionActions {
  return useConnectionStore.getState()
}

export function __resetConnectionForTests(): void {
  useConnectionStore.setState(initialState())
}
